import React, { useState, useEffect, useCallback } from 'react';
import { 
  getAccountsByStatus, 
  getAccountsByLevel, 
  getBlockerFrequency, 
  getCompletedOverTime, 
  getStaleHistory 
} from '../api';
import { toast } from 'sonner';
import { RefreshCw, Download, BarChart3, PieChart, TrendingUp, Clock } from 'lucide-react';
import { getStaleBadge } from '../components/account/helpers';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

// Extracted chart style constants to prevent re-render allocations
const CHART_AXIS_TICK = { fill: '#A1A1AA', fontSize: 12 };
const CHART_AXIS_TICK_SM = { fill: '#A1A1AA', fontSize: 11 };
const CHART_TOOLTIP_STYLE = { backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px' };
const CHART_TOOLTIP_LABEL = { color: '#E4E4E7' };
const CHART_GRID_STROKE = "#27272A";

const Reports = () => {
  const [activeTab, setActiveTab] = useState('status');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, levelRes, blockerRes, completedRes, staleRes] = await Promise.all([
        getAccountsByStatus(),
        getAccountsByLevel(),
        getBlockerFrequency(),
        getCompletedOverTime(),
        getStaleHistory()
      ]);
      
      setData({
        status: statusRes.data,
        level: levelRes.data,
        blockers: blockerRes.data,
        completed: completedRes.data,
        stale: staleRes.data
      });
    } catch (error) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToCSV = (dataArray, filename) => {
    if (!dataArray || dataArray.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(dataArray[0]);
    const csvContent = [
      headers.join(','),
      ...dataArray.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported');
  };

  const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899'];

  const tabs = [
    { id: 'status', label: 'By Status', icon: BarChart3 },
    { id: 'level', label: 'By Level', icon: PieChart },
    { id: 'blockers', label: 'Blocker Frequency', icon: BarChart3 },
    { id: 'completed', label: 'Completed Over Time', icon: TrendingUp },
    { id: 'stale', label: 'Stale History', icon: Clock },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">Reports</h1>
          <p className="text-text-secondary text-sm mt-1">Analytics and insights</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all"
            data-testid="refresh-reports"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 bg-surface p-1 rounded-lg border border-border" data-testid="report-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-highlight'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Report Content */}
      <div className="bg-surface border border-border rounded-lg p-6">
        {activeTab === 'status' && (
          <StatusReport data={data.status} onExport={() => exportToCSV(data.status, 'accounts_by_status')} />
        )}
        {activeTab === 'level' && (
          <LevelReport data={data.level} colors={COLORS} onExport={() => exportToCSV(data.level, 'accounts_by_level')} />
        )}
        {activeTab === 'blockers' && (
          <BlockerReport data={data.blockers} colors={COLORS} onExport={() => exportToCSV(data.blockers, 'blocker_frequency')} />
        )}
        {activeTab === 'completed' && (
          <CompletedReport data={data.completed} onExport={() => exportToCSV(data.completed, 'completed_over_time')} />
        )}
        {activeTab === 'stale' && (
          <StaleReport data={data.stale} onExport={() => exportToCSV(data.stale.map(a => ({
            account_number: a.account_number,
            account_name: a.account_name,
            level: a.account_level,
            team: a.current_team,
            last_activity: a.last_activity_date
          })), 'stale_accounts')} />
        )}
      </div>
    </div>
  );
};

const StatusReport = ({ data, onExport }) => {
  // Group by team
  const teams = ['Level 1 Tech', 'Level 2 Tech', 'Inbound Sales', 'Level 3 Tech'];
  const statuses = ['In Progress', 'Pending Review', 'On Hold', 'Blocked', 'Awaiting Client', 'Pending L3 Verification', 'Completed'];
  
  const chartData = teams.map(team => {
    const teamData = { team };
    statuses.forEach(status => {
      const found = data.find(d => d.team === team && d.status === status);
      teamData[status] = found?.count || 0;
    });
    return teamData;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary">Accounts by Status</h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-highlight text-text-primary rounded-md hover:bg-border transition-colors"
          data-testid="export-status"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
          <XAxis dataKey="team" tick={CHART_AXIS_TICK} />
          <YAxis tick={CHART_AXIS_TICK} />
          <Tooltip 
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL}
          />
          <Legend />
          <Bar dataKey="In Progress" stackId="a" fill="#3B82F6" />
          <Bar dataKey="Pending Review" stackId="a" fill="#8B5CF6" />
          <Bar dataKey="On Hold" stackId="a" fill="#71717A" />
          <Bar dataKey="Blocked" stackId="a" fill="#EF4444" />
          <Bar dataKey="Awaiting Client" stackId="a" fill="#F59E0B" />
          <Bar dataKey="Completed" stackId="a" fill="#10B981" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const LevelReport = ({ data, colors, onExport }) => {
  const chartData = data.map(d => ({
    name: `Level ${d.level}`,
    value: d.count
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary">Accounts by Level</h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-highlight text-text-primary rounded-md hover:bg-border transition-colors"
          data-testid="export-level"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      <div className="flex items-center justify-center">
        <ResponsiveContainer width="100%" height={400}>
          <RechartsPie>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL}
            />
          </RechartsPie>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const BlockerReport = ({ data, colors, onExport }) => {
  const chartData = data.map(d => ({
    name: `${d.blocker_type} (${d.device_type})`,
    count: d.count
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary">Blocker Frequency</h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-highlight text-text-primary rounded-md hover:bg-border transition-colors"
          data-testid="export-blockers"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      {chartData.length === 0 ? (
        <p className="text-text-secondary text-center py-8">No blocker data available</p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
            <XAxis type="number" tick={CHART_AXIS_TICK} />
            <YAxis type="category" dataKey="name" tick={CHART_AXIS_TICK_SM} width={150} />
            <Tooltip 
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL}
            />
            <Bar dataKey="count" fill="#EF4444" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const CompletedReport = ({ data, onExport }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary">Completed Over Time</h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-highlight text-text-primary rounded-md hover:bg-border transition-colors"
          data-testid="export-completed"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      {data.length === 0 ? (
        <p className="text-text-secondary text-center py-8">No completed accounts yet</p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
            <XAxis dataKey="date" tick={CHART_AXIS_TICK} />
            <YAxis tick={CHART_AXIS_TICK} />
            <Tooltip 
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL}
            />
            <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const StaleReport = ({ data, onExport }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-text-primary">Stale Accounts History</h3>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-highlight text-text-primary rounded-md hover:bg-border transition-colors"
          data-testid="export-stale"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      {data.length === 0 ? (
        <p className="text-text-secondary text-center py-8">No stale accounts</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stale-table">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2 text-left text-text-secondary">Account</th>
                <th className="px-4 py-2 text-left text-text-secondary">Name</th>
                <th className="px-4 py-2 text-left text-text-secondary">Level</th>
                <th className="px-4 py-2 text-left text-text-secondary">Team</th>
                <th className="px-4 py-2 text-left text-text-secondary">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((account) => (
                <tr key={account.id} className="hover:bg-surface-highlight">
                  <td className="px-4 py-2 font-mono text-text-primary">{account.account_number}</td>
                  <td className="px-4 py-2 text-text-primary">{account.account_name}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStaleBadge(account.account_level)}`}>
                      L{account.account_level}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-text-secondary">{account.current_team}</td>
                  <td className="px-4 py-2 text-text-secondary">{account.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Reports;
